import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { getBlogBySlug } from "@/data/blogData";

function ContentBlock({ block }) {
  if (block.type === "heading") {
    return (
      <h2
        className="font-display font-bold text-xl md:text-2xl mt-10 mb-4"
        style={{ color: "var(--color-text-dark)" }}
      >
        {block.text}
      </h2>
    );
  }

  if (block.type === "list") {
    return (
      <ul className="font-body text-base leading-relaxed my-4 pl-5 space-y-2 list-disc">
        {block.items.map((item, i) => (
          <li key={i} style={{ color: "var(--color-text-light)" }}>
            {item}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <p
      className="font-body text-base md:text-lg leading-relaxed my-4"
      style={{ color: "var(--color-text-light)" }}
    >
      {block.text}
    </p>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const blog = getBlogBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!blog) {
    return <Navigate to="/" replace />;
  }

  return (
    <article
      data-testid="blog-post-page"
      className="min-h-screen pt-28 pb-20 px-6"
      style={{ background: "var(--color-bg)" }}
    >
      <div className="max-w-3xl mx-auto">
        <Link
          to="/#blog"
          className="inline-flex items-center gap-2 font-display text-sm uppercase tracking-widest mb-10 transition-opacity hover:opacity-70"
          style={{ color: "var(--color-accent)" }}
          data-testid="blog-back-link"
        >
          <ArrowLeft size={16} />
          Back to Blog
        </Link>

        <header className="mb-10">
          <span
            className="inline-block font-body text-xs uppercase tracking-widest px-3 py-1 rounded-full mb-5"
            style={{
              background: "var(--color-secondary)",
              color: "var(--color-accent)",
            }}
          >
            {blog.category}
          </span>

          <h1
            className="font-display font-bold leading-tight mb-5"
            style={{
              fontSize: "clamp(1.75rem, 4vw, 2.75rem)",
              color: "var(--color-text-dark)",
            }}
            data-testid="blog-post-title"
          >
            {blog.title}
          </h1>

          <div
            className="flex items-center gap-2 font-body text-sm"
            style={{ color: "var(--color-text-light)" }}
          >
            <Clock size={14} />
            <span>{blog.date}</span>
            <span>·</span>
            <span>{blog.readTime}</span>
          </div>

          <div
            className="w-20 h-[3px] mt-8 rounded-full"
            style={{ background: "var(--color-accent)" }}
          />
        </header>

        <div
          className="rounded-2xl p-8 md:p-12 bg-white"
          style={{
            border: "1px solid #e3efe9",
            boxShadow: "0 10px 28px -18px rgba(31,51,41,0.12)",
          }}
        >
          {blog.content.map((block, i) => (
            <ContentBlock key={i} block={block} />
          ))}
        </div>

        <div
          className="mt-12 p-8 rounded-2xl text-center"
          style={{ background: "var(--color-secondary)" }}
        >
          <p
            className="font-body text-base mb-4"
            style={{ color: "var(--color-text-dark)" }}
          >
            Need professional support? Dr. Fiza Hasny is here to help.
          </p>
          <Link
            to="/#contact"
            className="inline-block font-display text-sm uppercase tracking-widest font-semibold px-8 py-3 rounded-full transition-opacity hover:opacity-90"
            style={{
              background: "var(--color-accent)",
              color: "var(--color-white)",
            }}
          >
            Book a Consultation
          </Link>
        </div>
      </div>
    </article>
  );
}
